/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Chat reassignment side effects after admin reassigns a conversation.
 * @module logic/chat/chatReassign
 */

import { logger, sanitizeError } from "../../utils/logger.js";
import { fetchUserChats } from "./chatApi.js";

/**
 * Completes chat reassignment by refreshing the inbox and clearing the active selection.
 * @param {Object} options
 * @returns {Promise<boolean>} True when reassignment succeeded
 */
export const completeChatReassignment = async ({
  result,
  navigate,
  setChats,
  setSelectedChat,
  setSelectedUser,
  setShowReassignModal,
}) => {
  if (!result?.success) {
    logger.error("Failed to reassign chat:", { data: result?.message });
    return false;
  }

  logger.info("Chat reassigned successfully");

  try {
    const userId = localStorage.getItem("userId");
    if (userId) {
      await fetchUserChats(userId, navigate, setChats);
      logger.info("Chats list refreshed after reassignment");
    }
  } catch (error) {
    logger.error("Error refreshing chats after reassignment:", {
      err: sanitizeError(error),
    });
  }

  setSelectedChat(null);
  setSelectedUser(null);
  setShowReassignModal(false);
  return true;
};
